import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { GET_FRAUD_CASES, RESOLVE_FRAUD_CASE } from '../../api/fraud-cases-api';
import { ShieldAlert, User, Building2, Package, Tag, Clock, ChevronDown, ChevronUp, RefreshCw, CheckCircle2, X } from 'lucide-react';
import UserDetailModal from '../../components/admin/users/UserDetailModal';

const fmt = (d) =>
  d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'medium' }) : '—';

const userName = (u) =>
  u ? `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email : '—';

const FRAUD_READ_KEY = 'fraud_read_ids';

const getReadIds = () => {
  try { return new Set(JSON.parse(localStorage.getItem(FRAUD_READ_KEY) || '[]')); }
  catch { return new Set(); }
};

const markRead = (id) => {
  const ids = getReadIds();
  ids.add(id);
  localStorage.setItem(FRAUD_READ_KEY, JSON.stringify([...ids]));
  window.dispatchEvent(new Event('fraud-read-updated'));
};

const ResolveModal = ({ caseId, onClose, onResolved }) => {
  const [reason, setReason] = useState('');
  const [resolveFraudCase, { loading }] = useMutation(RESOLVE_FRAUD_CASE);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) return;
    try {
      await resolveFraudCase({ variables: { id: caseId, reason: reason.trim() } });
      toast.success('Cas de fraude résolu');
      onResolved();
      onClose();
    } catch (err) {
      toast.error('Erreur lors de la résolution');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <CheckCircle2 size={18} className="text-green-600" />
            Résoudre ce cas de fraude
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Raison de résolution <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              required
              placeholder="Décrivez la raison de résolution de ce cas..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading || !reason.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-green-700 hover:bg-green-800 disabled:opacity-50 rounded-lg flex items-center gap-2"
            >
              <CheckCircle2 size={14} />
              {loading ? 'Résolution…' : 'Confirmer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const FraudCaseRow = ({ fc, onUserClick, onRefetch }) => {
  const [expanded, setExpanded] = useState(() => getReadIds().has(fc.id));
  const [showResolveModal, setShowResolveModal] = useState(false);
  const isResolved = fc.status === 'RESOLVED';

  const handleToggle = () => {
    if (!expanded) markRead(fc.id);
    setExpanded(v => !v);
  };

  return (
    <>
      <div className={`border rounded-xl shadow-sm overflow-hidden mb-3 ${isResolved ? 'bg-gray-50 border-gray-200' : 'bg-white border-red-100'}`}>
        {/* Summary row */}
        <button
          className={`w-full text-left px-5 py-4 flex items-center justify-between transition-colors ${isResolved ? 'hover:bg-gray-100' : 'hover:bg-red-50'}`}
          onClick={handleToggle}
        >
          <div className="flex items-center gap-4 flex-wrap">
            {isResolved ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-green-100 text-green-700 px-2.5 py-1 rounded-full">
                <CheckCircle2 size={12} />
                Résolu #{fc.id.slice(-6).toUpperCase()}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-red-100 text-red-700 px-2.5 py-1 rounded-full">
                <ShieldAlert size={12} />
                Fraude #{fc.id.slice(-6).toUpperCase()}
              </span>
            )}
            <span className="flex items-center gap-1.5 text-sm text-gray-700">
              <Package size={14} className="text-gray-400" />
              <span className="font-medium">{fc.product?.name}</span>
            </span>
            <span className="flex items-center gap-1.5 text-sm text-gray-700">
              <Tag size={14} className="text-gray-400" />
              {fc.promotion?.name}
            </span>
            <span className="flex items-center gap-1.5 text-sm text-gray-500">
              <Clock size={14} className="text-gray-400" />
              {fmt(fc.createdAt)}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {!isResolved && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowResolveModal(true); }}
                className="flex items-center gap-1.5 text-xs font-semibold bg-green-700 text-white px-3 py-1.5 rounded-lg hover:bg-green-800 transition-colors"
              >
                <CheckCircle2 size={13} />
                Résoudre
              </button>
            )}
            {expanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
          </div>
        </button>

        {/* Detail panel */}
        {expanded && (
          <div className="border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100">
            {/* User A */}
            <div className="p-5 bg-red-50">
              <p className="text-xs font-bold text-red-600 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                <User size={12} /> Utilisateur A — bloqué
              </p>
              <div className="space-y-1.5 text-sm">
                <p>
                  <span className="text-gray-500">Nom :</span>{' '}
                  <button
                    onClick={() => onUserClick(fc.userA)}
                    className="font-medium text-blue-600 hover:underline"
                  >
                    {userName(fc.userA)}
                  </button>
                </p>
                <p><span className="text-gray-500">Email :</span> {fc.userA?.email}</p>
                <p className="flex items-center gap-1.5">
                  <Building2 size={13} className="text-gray-400" />
                  <span className="text-gray-500">Pharmacie :</span>
                  <span className="font-medium">{fc.pharmacyA?.namePharmacy ?? '—'}</span>
                </p>
                {fc.pharmacyA?.address && (
                  <p className="text-gray-400 text-xs ml-5">{fc.pharmacyA.address}</p>
                )}
                <p className="flex items-center gap-1.5 mt-2 text-red-700">
                  <Clock size={13} />
                  Scanné le : <span className="font-semibold">{fmt(fc.scannedAtA)}</span>
                </p>
              </div>
            </div>

            {/* User B */}
            <div className="p-5 bg-orange-50">
              <p className="text-xs font-bold text-orange-600 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                <User size={12} /> Utilisateur B — commission reçue
              </p>
              <div className="space-y-1.5 text-sm">
                <p>
                  <span className="text-gray-500">Nom :</span>{' '}
                  <button
                    onClick={() => onUserClick(fc.userB)}
                    className="font-medium text-blue-600 hover:underline"
                  >
                    {userName(fc.userB)}
                  </button>
                </p>
                <p><span className="text-gray-500">Email :</span> {fc.userB?.email}</p>
                <p className="flex items-center gap-1.5">
                  <Building2 size={13} className="text-gray-400" />
                  <span className="text-gray-500">Pharmacie :</span>
                  <span className="font-medium">{fc.pharmacyB?.namePharmacy ?? '—'}</span>
                </p>
                {fc.pharmacyB?.address && (
                  <p className="text-gray-400 text-xs ml-5">{fc.pharmacyB.address}</p>
                )}
                <p className="flex items-center gap-1.5 mt-2 text-orange-700">
                  <Clock size={13} />
                  Scanné le : <span className="font-semibold">{fmt(fc.scannedAtB)}</span>
                </p>
              </div>
            </div>

            {/* QR code + resolution info */}
            <div className="p-5 md:col-span-2 bg-gray-50 space-y-3">
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Code QR</p>
                <code className="text-sm font-mono bg-white border border-gray-200 px-3 py-1.5 rounded-lg text-gray-700">
                  {fc.label?.code}
                </code>
              </div>
              {isResolved && (
                <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                  <p className="text-xs font-bold text-green-700 uppercase tracking-wide mb-1 flex items-center gap-1.5">
                    <CheckCircle2 size={12} /> Résolu le {fmt(fc.resolvedAt)}
                  </p>
                  <p className="text-sm text-green-800">{fc.resolutionReason}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showResolveModal && (
        <ResolveModal
          caseId={fc.id}
          onClose={() => setShowResolveModal(false)}
          onResolved={onRefetch}
        />
      )}
    </>
  );
};

const FraudCases = () => {
  const { data, loading, error, refetch } = useQuery(GET_FRAUD_CASES, {
    fetchPolicy: 'network-only',
  });

  const cases = data?.fraudCases ?? [];
  const pendingCount = cases.filter(fc => fc.status === 'PENDING').length;
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-100 rounded-lg">
            <ShieldAlert size={22} className="text-red-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Registre des fraudes</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              QR codes scannés par des utilisateurs de pharmacies différentes
            </p>
          </div>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-200 hover:border-gray-300 px-3 py-2 rounded-lg transition-colors"
        >
          <RefreshCw size={14} />
          Actualiser
        </button>
      </div>

      {/* Stats banner */}
      <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-4 mb-6 flex items-center gap-3">
        <ShieldAlert size={20} className="text-red-500" />
        <p className="text-sm text-red-700">
          <span className="font-bold">{cases.length}</span> cas de fraude détecté{cases.length > 1 ? 's' : ''} ·{' '}
          <span className="font-bold text-red-800">{pendingCount}</span> en attente de résolution.
          Pour chaque cas, l'utilisateur A a été automatiquement bloqué et l'utilisateur B a reçu sa commission sans double facturation de Mazid.
        </p>
      </div>

      {/* Content */}
      {loading && (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500" />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-red-700 text-sm">
          Erreur lors du chargement : {error.message}
        </div>
      )}

      {!loading && !error && cases.length === 0 && (
        <div className="text-center py-20 text-gray-400">
          <ShieldAlert size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-base font-medium">Aucune fraude détectée</p>
          <p className="text-sm mt-1">Tout semble normal.</p>
        </div>
      )}

      {!loading && cases.map((fc) => (
        <FraudCaseRow key={fc.id} fc={fc} onUserClick={handleUserClick} onRefetch={refetch} />
      ))}

      <UserDetailModal
        user={selectedUser}
        isOpen={showUserModal}
        onClose={() => { setShowUserModal(false); setSelectedUser(null); }}
      />
    </div>
  );
};

export default FraudCases;
